import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
} from '@startupquickstart/frontend';
import { Link } from 'react-router-dom';

import { EventStatusBadge } from '@/components/events';
import { EventRealtimeToggles } from '@/components/events/EventRealtimeToggles';
import { EventScheduleCard } from '@/components/events/EventScheduleCard';
import { ReadinessChecklistCard } from '@/components/events/ReadinessChecklistCard';
import { RichTextDisplay, VisibilityBadge } from '@/components/shared';
import { useEventContext } from '@/pages/events/useEventContext';

export function EventOverviewTab() {
  const { event, isLoading } = useEventContext();

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 rounded-xl" />
        <Skeleton className="h-48 rounded-xl" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Event not found</p>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 overflow-auto -m-4 sm:-m-6 p-4 sm:p-6 space-y-6">
      {/* Pre-launch readiness checklist (draft/scheduled only) */}
      {(event.status === 'draft' || event.status === 'scheduled') && (
        <ReadinessChecklistCard eventId={event.id} />
      )}

      {/* Event Info */}
      <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
        <CardHeader className="px-6 py-4 !pb-4 border-b">
          <CardTitle>Event Info</CardTitle>
          <CardDescription>Event details and configuration</CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <p className="text-sm text-muted-foreground mb-1">Status</p>
              <EventStatusBadge status={event.status} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground mb-1">Visibility</p>
              <VisibilityBadge visibility={event.visibility} />
            </div>
            {event._count?.teams !== undefined && (
              <div>
                <p className="text-sm text-muted-foreground mb-1">Teams</p>
                <span className="text-sm font-medium">{event._count.teams}</span>
              </div>
            )}
            {event.template && (
              <div>
                <p className="text-sm text-muted-foreground mb-1">Template</p>
                <Link
                  to={`/templates/${event.template.id}`}
                  className="text-sm font-medium text-primary hover:underline"
                >
                  {event.template.name}
                </Link>
              </div>
            )}
          </div>
          {/* Live-tunable switches — shared with the Live tab */}
          <EventRealtimeToggles event={event} className="mt-6 border-t pt-6 lg:grid-cols-3" />
        </CardContent>
      </Card>

      <EventScheduleCard event={event} />

      {/* Description */}
      {event.description && (
        <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
          <CardHeader className="px-6 py-4 !pb-4 border-b">
            <CardTitle>Description</CardTitle>
            <CardDescription>Event overview</CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <RichTextDisplay content={event.description} />
          </CardContent>
        </Card>
      )}

      {/* Media */}
      {(event.coverImageUrl || event.introVideoUrl) && (
        <Card className="rounded-xl border bg-card shadow-sm py-0 gap-0">
          <CardHeader className="px-6 py-4 !pb-4 border-b">
            <CardTitle>Media</CardTitle>
            <CardDescription>Cover image and intro video</CardDescription>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid gap-4 sm:grid-cols-2">
              {event.coverImageUrl && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Cover Image</p>
                  <img
                    src={event.coverImageUrl}
                    alt={event.name}
                    className="rounded-lg max-h-48 object-cover"
                  />
                </div>
              )}
              {event.introVideoUrl && (
                <div>
                  <p className="text-sm text-muted-foreground mb-2">Intro Video URL</p>
                  <a
                    href={event.introVideoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-primary hover:underline"
                  >
                    {event.introVideoUrl}
                  </a>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
